/**
 * Inbox UI store — Zustand 5 + immer + persist.
 *
 * Holds the *transient UI* concerns the inbox surface needs to keep alive
 * across remounts (e.g. tab snapshots that make the unread tab sticky) and the
 * dedup tape that survives a refresh so reconnection sync can replay safely.
 *
 * Persist whitelist — `partialize` at the foot of this file is the authority;
 * this is the rationale. It has outgrown the original "`lastEventAt` ONLY":
 *   - `lastEventAt` / `lastScheduledAt` — the sync and schedule watermarks.
 *   - The three per-conversation view maps that together answer "put me back
 *     where I was" after F5: which channel tab, which email chain, and whether
 *     the chain list was open. Each is bounded by `capThreadViewState`.
 *   - The dedup tape (`recentEvents`) is ephemeral in-memory state — a `Set`
 *     can't round-trip through JSON, and the `lastEventAt` watermark already
 *     gates the reconnect sync replay, so the tape doesn't need to survive a
 *     refresh.
 *   - Everything else is in-memory; refreshing should NOT remember which
 *     conversation was open or the draft buffer. Drafts live with the form
 *     library at the component level.
 *
 * Hard rules (mirror Streams F/G/H contracts):
 *   - Selectors at use sites. We don't expose convenience selectors here.
 *   - All mutators are sync; immer drafts let us write them in a "mutating"
 *     style without violating the readonly types on `IUnifiedConversation`.
 *   - The store is feature-scoped — exported only via `features/inbox/index.ts`.
 *     If another feature needs any of this, it's a refactor signal.
 */
import '@/lib/immer-setup';

import { castDraft, enableMapSet } from 'immer';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { MessageChannel, MessageDirection } from '../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Dedup tape size — matches the dedup-chain budget in §227 of the plan. */
const RECENT_EVENT_IDS_LIMIT = 200;

/** Composer mode toggle in the message composer. */
export type ComposerMode = 'reply' | 'note';

/** Active tab in the right-hand contact panel. */
export type ContactPanelTab = 'profile' | 'notes' | 'ai-insights' | 'starred';

/**
 * A queued "scroll the thread to this starred message" request — a one-shot
 * handoff from the Starred tab (right panel) to the thread (middle pane), the
 * same shape as {@link IPendingComposerInsert}. `nonce` makes a second click on
 * the same entry a fresh request rather than stale state.
 */
export interface IPendingStarJump {
    readonly unifiedId: string;
    readonly messageId: string;
    readonly channel: MessageChannel;
    /** Email only — the subject chain to open before looking for the card. */
    readonly emailThreadId: string | null;
    readonly nonce: string;
}

/** A queued "put this in the composer" request. See `pendingComposerInsert`. */
export interface IPendingComposerInsert {
    readonly unifiedId: string;
    /** Only the composer showing this channel consumes it. */
    readonly channel: MessageChannel;
    /** Plain text for SMS/WhatsApp; HTML for email. */
    readonly body: string;
    /** Email only — fills the Subject row alongside the body. */
    readonly subject?: string;
    /** Email only — prefills the recipient rows. Bare addresses; the composer
     *  rebuilds chips from them the same way a restored draft does. */
    readonly toEmails?: readonly string[];
    readonly ccEmails?: readonly string[];
    readonly bccEmails?: readonly string[];
    /**
     * The still-scheduled message this insert is EDITING, if any.
     *
     * Scheduled email content is frozen server-side —
     * `inbox-message-schedule.service.ts` rejects any body edit on an EMAIL row
     * and its own comment prescribes "cancel it and schedule a new one". So an
     * edit is a fresh compose that, once it dispatches, cancels the row it came
     * from. Set, the composer REPLACES its body rather than inserting at the
     * caret: an edit starts from the scheduled text, it does not append to it.
     */
    readonly replacesScheduledMessageId?: string;
    readonly nonce: string;
}

/**
 * Files the WhatsApp attach menu has handed to the full-panel send preview.
 *
 * A one-shot handoff in the same shape as {@link IPendingComposerInsert}, and
 * for the same reason: the `<input type="file">` belongs to the composer's
 * attach menu, while the preview that shows and captions those files covers
 * the whole thread pane and is mounted by `ThreadPane`. Neither holds a
 * reference to the other.
 *
 * `File` objects are not serializable, which is safe here only because
 * `partialize` persists nothing but the two watermarks — see the note there
 * before adding this key to it.
 */
export interface IPendingMediaSend {
    readonly unifiedId: string;
    readonly files: readonly File[];
    /** Distinguishes two consecutive picks of the same file. */
    readonly nonce: string;
}

/**
 * A WhatsApp message the composer is replying to, pinned above the input.
 *
 * Sticky, unlike the one-shot {@link IPendingComposerInsert}: it must survive
 * while the agent types and is cleared only by sending or dismissing.
 *
 * The display fields are a snapshot frozen at click time, not cached server
 * state — same as `IPendingComposerInsert.body`, which is also derived text
 * living in this store. Do not "fix" this into a query; the quoted message is
 * often outside the loaded page and re-resolving it would need a fetch.
 */
export interface IQuotedReplyTarget {
    readonly messageId: string;
    readonly senderName: string;
    readonly preview: string;
    /** With `createdAt`, enough to draw the quote on the optimistic bubble. */
    readonly direction: MessageDirection;
    readonly createdAt: string;
}

export type ConnectionState = 'connecting' | 'connected' | 'disconnected';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

/**
 * Bounded FIFO dedup tape backed by a `Set` for O(1) membership checks.
 *
 *   - `set` answers `hasSeenEvent` in O(1) (vs the old `Array.includes` walk).
 *   - `queue` preserves insertion order so we can evict the oldest id once the
 *     tape exceeds `RECENT_EVENT_IDS_LIMIT` — both structures stay in sync.
 *
 * Immer drafts a `Set` as soon as one is READ off a draft, which needs the
 * MapSet plugin that `@/lib/immer-setup` loads — imported at the top of this
 * file. `recordEvent` still rebuilds both structures and replaces the holder
 * wholesale, which keeps the two in lockstep through FIFO eviction.
 */
// This store puts `Set`s into immer drafts (the dedup tape and both tab
// snapshots). Immer refuses to finalise a draft containing one unless its
// MapSet plugin is loaded — the failure is an opaque "[Immer] minified error
// nr: 0" thrown from whichever action touched a Set first. Idempotent, and
// called at module load so it runs before the store below is created.
enableMapSet();

export interface IRecentEvents {
    readonly set: ReadonlySet<string>;
    readonly queue: readonly string[];
}

export interface IInboxUiState {
    // --- Persisted (sessionStorage) -----------------------------------------
    /** ISO timestamp of the most recent event the cache acknowledged. */
    lastEventAt: string | null;
    /**
     * ISO datetime of the last time the user scheduled a message — surfaced as
     * the "Last used" preset in the schedule dropdown (shown only when still in
     * the future).
     */
    lastScheduledAt: string | null;

    // --- In-memory only ------------------------------------------------------
    /**
     * Bounded FIFO dedup tape — first stage of the dedup chain. Ephemeral: not
     * persisted (a `Set` can't round-trip JSON, and `lastEventAt` already gates
     * the reconnect replay).
     */
    recentEvents: IRecentEvents;
    activeUnifiedConversationId: string | null;
    /**
     * Sticky snapshot of unified IDs that were visible in the Unread tab when
     * the user entered it — those rows STAY in the strip even after they're
     * marked read while the tab is active. Cleared on `leaveTab('unread')`.
     */
    unreadTabSnapshot: ReadonlySet<string>;
    /** Symmetric snapshot for the Read tab. */
    readTabSnapshot: ReadonlySet<string>;
    /**
     * Composer drafts keyed by `${unifiedId}:${channel}`. Survives switching
     * conversations but NOT a refresh (we deliberately do not persist drafts
     * — that's a forms-library concern, not a Zustand concern).
     */
    drafts: Readonly<Record<string, string>>;
    composerMode: ComposerMode;
    /**
     * Active tab in the right-hand contact panel (Profile vs Notes). Lifted to
     * the store so selecting internal-note mode in the thread composer can flip
     * the panel to Notes.
     */
    contactPanelTab: ContactPanelTab;
    /**
     * Which channel the composer is currently targeting in a multi-channel
     * thread. `null` defaults to the unified's last-message channel.
     */
    selectedComposerChannel: MessageChannel | null;
    /**
     * The channel each conversation was last left on, keyed by unified
     * conversation id — the refresh restore for `selectedComposerChannel`.
     *
     * Persisted, capped, alongside the two email-view maps below, and the
     * outermost of the three: restoring WHICH CHAIN was open is dead weight if
     * the Email reader itself never renders. Without this a refresh fell all the
     * way through to `pickDefaultChannel`, where a single unread SMS outranks
     * the email chain the user was reading (ClickUp 14ygtkj6jg0).
     *
     * A conversation whose channel was cleared — the contact panel's Channels
     * list toggles back to the merged stream — has NO key here. Storing `null`
     * would make "cleared on purpose" indistinguishable from "never chosen", and
     * only the second may fall through to the heuristic.
     */
    selectedChannelByConversation: Readonly<Record<string, MessageChannel>>;
    /**
     * Chosen sending number per thread, keyed by unified conversation id.
     * Empty means "whatever number this thread already runs on".
     *
     * Deliberately NOT persisted: it is a per-thread map that would grow
     * without bound in localStorage, and sticking for the session is all the
     * composer needs. Also load-bearing beyond the picker's own UI — the
     * composer resolves WHICH child conversation to send on from this, because
     * sending under a new identity upserts a second child for the same thread
     * and a blind `find(c => c.channel === …)` would then be order-dependent.
     */
    selectedIdentityByThread: Readonly<Record<string, string>>;
    /**
     * Which email subject chain the Email tab is showing, per unified
     * conversation. `null`/absent means "the newest one" — opening a contact
     * lands directly in their most recent chain rather than on a list, and the
     * back arrow is what reveals the list.
     *
     * Session-only, same rationale as `selectedIdentityByThread`: a per-thread
     * map would grow without bound in localStorage.
     */
    activeEmailThreadByConversation: Readonly<Record<string, string | null>>;
    /**
     * Whether the Email tab is showing the chain LIST instead of a chain.
     * Separate from `activeEmailThreadByConversation` because "no chain
     * selected yet" and "the user pressed back to browse" are different states:
     * the first auto-selects the newest chain, the second must not.
     */
    emailThreadListOpenByConversation: Readonly<Record<string, boolean>>;
    /**
     * Whether the filter rail's slide-over is open. Only meaningful below `xl`,
     * where the rail is not docked beside the list — above that it stays false
     * and the rail is simply visible.
     */
    filterRailOpen: boolean;
    /**
     * Whether the thread composer is expanded, per unified conversation.
     * Absent key = collapsed (the default): the composer renders as a
     * single-line "Click to expand and reply…" bar with channel icons.
     * Session-only — mirrors `selectedIdentityByThread`'s no-persist rationale.
     */
    composerExpandedByThread: Readonly<Record<string, boolean>>;
    /**
     * Whether an AI employee is drafting a reply right now, per unified
     * conversation. Fed by the transient `inbox:ai:activity` socket event; read
     * by the approval banner so a supervised thread says so instead of sitting
     * silent through model time.
     *
     * Session-only and deliberately NOT derived from `aiState` — that is the
     * durable mode the composer gates on, and this is a momentary fact about
     * one run. Absent key = not drafting.
     */
    aiDraftingByThread: Readonly<Record<string, boolean>>;
    /**
     * Which compose-new surface is open (the conversation-list composer
     * button). Non-null replaces the thread + contact panes with the
     * full-panel composer (Stitch takeover layout) — NOT a modal.
     */
    composeChannel: 'SMS' | 'WHATSAPP' | 'EMAIL' | null;
    /**
     * Whether the open compose panel holds unsent content. Reported by the
     * panel itself; read by the conversation list, which otherwise navigates
     * "silently" (the panel is store state, not a route, so it stays mounted
     * over the thread pane and the click looks like it did nothing).
     */
    composeDirty: boolean;
    /**
     * Compact (mobile/tablet, < xl) only: whether the full-screen contact
     * detail view is open on top of the thread. Ignored at ≥ xl, where the
     * contact panel is always docked. Reset whenever the active conversation
     * changes (see `setActiveUnifiedConversationId`).
     */
    compactContactOpen: boolean;
    /**
     * Note id to briefly flash after navigating in from a mention notification
     * (`/inbox/:id?note=:noteId`). Set by the conversation page; consumed and
     * cleared by the notes section once it scrolls the card into view.
     */
    highlightNoteId: string | null;
    /**
     * Text waiting to be dropped into the composer, set by the AI Insights
     * tab's "Insert into composer" action.
     *
     * A one-shot handoff rather than a direct write, because the two live in
     * different panes: the insight card is in the contact panel on the right,
     * the composer is under the thread in the middle, and neither holds a
     * reference to the other. Same shape of solution as `highlightNoteId`.
     *
     * `nonce` makes repeat inserts of identical text distinct, so pressing the
     * button twice inserts twice — without it the second press would look
     * like stale state and be ignored. The consuming composer clears it.
     */
    pendingComposerInsert: IPendingComposerInsert | null;
    /** Starred-message jump waiting for the thread to land it. */
    pendingStarJump: IPendingStarJump | null;
    /**
     * Quoted-reply target per thread. Keyed so a target can only ever be read
     * by the thread it was set on.
     *
     * Cleared when the agent switches conversation, together with
     * `composerExpandedByThread`: the composer comes back collapsed, and a
     * collapsed composer must not be carrying a reply target nobody can see.
     * The typed text is a separate thing (`drafts`) and still survives.
     */
    replyTargetByThread: Record<string, IQuotedReplyTarget>;
    /**
     * Files awaiting the WhatsApp send-preview panel, or null when it is
     * closed. See {@link IPendingMediaSend}.
     */
    pendingMediaSend: IPendingMediaSend | null;

    // --- Actions -------------------------------------------------------------
    setActiveUnifiedConversationId: (id: string | null) => void;
    setComposerMode: (mode: ComposerMode) => void;
    setContactPanelTab: (tab: ContactPanelTab) => void;
    setSelectedComposerChannel: (channel: MessageChannel | null) => void;
    setSelectedIdentity: (unifiedId: string, channelIdentityId: string) => void;
    /** Open one email subject chain (and leave the chain list). */
    setActiveEmailThread: (unifiedId: string, threadId: string | null) => void;
    /** Back arrow ⇄ chain selection. */
    setEmailThreadListOpen: (unifiedId: string, open: boolean) => void;
    setComposerExpanded: (unifiedId: string, expanded: boolean) => void;
    setAiDrafting: (unifiedId: string, drafting: boolean) => void;
    setComposeChannel: (channel: 'SMS' | 'WHATSAPP' | 'EMAIL' | null) => void;
    setComposeDirty: (dirty: boolean) => void;
    /**
     * Select the target channel (or note mode) AND expand, in one atomic set()
     * so the expanded composer mounts already pointing at the target.
     *
     * Callers: the per-message Reply / Reply-all / AI Reply buttons
     * (`ThreadPane`), and the collapsed bar's Internal note icon.
     *
     * It expands UNCONDITIONALLY, on purpose. Those Reply buttons render only
     * inside the Email reader — the merged stream's `EmailMessageCard` is passed
     * no `onReply`/`onAiReply`, and the card gates its action bar on both — so
     * they are always same-channel and `selectComposerChannel`'s rule would be a
     * no-op there today. The contract stays explicit anyway: a Reply button that
     * silently declined to open the composer is a dead control, and that is one
     * refactor away the day someone passes `onReply` to the merged-stream card.
     *
     * The collapsed bar's CHANNEL icons deliberately do NOT use this — see
     * `selectComposerChannel` (ClickUp 14ygtkj6je6).
     */
    expandComposerTo: (unifiedId: string, target: MessageChannel | 'note') => void;
    /**
     * Collapsed-bar channel-icon click: NAVIGATE first, compose second.
     *
     * A tap on a channel the reader is not showing only selects it — the thread
     * pane switches to that channel and the composer stays collapsed. Tapping
     * the channel already showing is the second half of the gesture and expands.
     *
     * Split out of `expandComposerTo` because fusing the two meant a
     * cross-channel tap performed a navigation and an expansion in the same
     * commit, so the user never perceived the navigation: from an SMS thread the
     * Email icon just produced a full-height email composer over a conversation
     * they had not asked to leave (ClickUp 14ygtkj6je6).
     *
     * "Already showing" is `selectedComposerChannel` RAW, because that is the
     * value the reader itself keys off (`ThreadPane`'s `isEmailChannel`, and its
     * `!selectedChannel` merged-stream branch) — not `ThreadComposer`'s
     * `effectiveChannel`, whose `availableChildren[0]` fallback is an arbitrary
     * API ordering rather than anything on screen. `null` is a real and sticky
     * state (the contact panel's Channels list clears it) meaning "the merged
     * stream", where every tap is a genuine navigation.
     *
     * Deliberately NOT mode-aware: `composerMode` is invisible while collapsed,
     * so gating on it would turn a same-channel tap after leaving note mode into
     * a click that appears to do nothing.
     *
     * Internal note is out of scope and still routes through `expandComposerTo`
     * — hence `MessageChannel`, not `| 'note'`.
     */
    selectComposerChannel: (unifiedId: string, target: MessageChannel) => void;
    setFilterRailOpen: (open: boolean) => void;
    setCompactContactOpen: (open: boolean) => void;
    setHighlightNoteId: (id: string | null) => void;
    /**
     * Queue text for the composer and open it on the right channel, in one
     * atomic set() — the composer has to mount already pointing at the target
     * channel, or it will not recognise the insert as its own.
     */
    requestComposerInsert: (insert: IPendingComposerInsert) => void;
    clearComposerInsert: () => void;
    /**
     * Jump the thread to a starred message: switch to its channel tab (and, for
     * email, open its subject chain) in the same set() that queues the jump, so
     * the thread mounts already showing the right stream.
     */
    requestStarJump: (jump: IPendingStarJump) => void;
    clearStarJump: () => void;
    /** Hand picked files to the send-preview panel, which then owns them. */
    openMediaPreview: (unifiedId: string, files: readonly File[]) => void;
    /** Close the panel, discarding whatever it was holding. */
    clearMediaPreview: () => void;
    /**
     * Point the composer at a message to quote. Atomic like
     * `requestComposerInsert`: the composer must already be expanded and on
     * the WhatsApp tab, or it will not recognise the target as its own.
     */
    startQuotedReply: (unifiedId: string, target: IQuotedReplyTarget) => void;
    clearQuotedReply: (unifiedId: string) => void;
    setDraft: (unifiedId: string, channel: MessageChannel, body: string) => void;
    clearDraft: (unifiedId: string, channel: MessageChannel) => void;
    /** Remember the last scheduled time for the "Last used" preset. */
    setLastScheduledAt: (iso: string) => void;

    /**
     * Sticky tab snapshots.
     *   - `enterTab(tab, ids)` seeds the snapshot with the IDs currently
     *     visible. Stream H consults `unreadTabSnapshot` to decide whether to
     *     keep a now-read row in the unread strip.
     *   - `leaveTab(tab)` clears it.
     */
    enterTab: (tab: 'unread' | 'read', ids: readonly string[]) => void;
    leaveTab: (tab: 'unread' | 'read') => void;

    /**
     * Dedup tape mutators. `hasSeenEvent` is a *query* — does NOT mutate.
     * `recordEvent` pushes a new id, evicts the oldest beyond the limit, and
     * bumps `lastEventAt`.
     */
    hasSeenEvent: (eventId: string) => boolean;
    recordEvent: (eventId: string, eventAt: string) => void;

    /** Reset the entire store — used on logout or org-switch. */
    reset: () => void;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

function makeInitialState(): Pick<
    IInboxUiState,
    | 'lastEventAt'
    | 'lastScheduledAt'
    | 'recentEvents'
    | 'activeUnifiedConversationId'
    | 'unreadTabSnapshot'
    | 'readTabSnapshot'
    | 'drafts'
    | 'composerMode'
    | 'contactPanelTab'
    | 'selectedComposerChannel'
    | 'selectedChannelByConversation'
    | 'selectedIdentityByThread'
    | 'activeEmailThreadByConversation'
    | 'emailThreadListOpenByConversation'
    | 'composerExpandedByThread'
    | 'aiDraftingByThread'
    | 'composeChannel'
    | 'composeDirty'
    | 'filterRailOpen'
    | 'compactContactOpen'
    | 'highlightNoteId'
    | 'pendingComposerInsert'
    | 'pendingStarJump'
    | 'replyTargetByThread'
    | 'pendingMediaSend'
> {
    return {
        lastEventAt: null,
        lastScheduledAt: null,
        recentEvents: { set: new Set<string>(), queue: [] },
        activeUnifiedConversationId: null,
        unreadTabSnapshot: new Set<string>(),
        readTabSnapshot: new Set<string>(),
        drafts: {},
        composerMode: 'reply',
        contactPanelTab: 'profile',
        selectedComposerChannel: null,
        selectedChannelByConversation: {},
        selectedIdentityByThread: {},
        activeEmailThreadByConversation: {},
        emailThreadListOpenByConversation: {},
        composerExpandedByThread: {},
        aiDraftingByThread: {},
        composeChannel: null,
        composeDirty: false,
        filterRailOpen: false,
        compactContactOpen: false,
        highlightNoteId: null,
        pendingComposerInsert: null,
        pendingStarJump: null,
        replyTargetByThread: {},
        pendingMediaSend: null,
    };
}

function draftKey(unifiedId: string, channel: MessageChannel): string {
    return `${unifiedId}:${channel}`;
}

/**
 * How many conversations' email-view state survives a refresh. The maps
 * themselves grow for as long as the tab lives, and `persist` re-serialises the
 * whole partialized slice on EVERY state change — so what is written has to be
 * bounded even though `sessionStorage` would not itself complain.
 *
 * 50 is "everything the user plausibly touched before hitting refresh".
 */
const PERSISTED_THREAD_VIEW_LIMIT = 50;

/**
 * The most recently added entries, oldest dropped. Relies on JS objects keeping
 * string keys in insertion order, which is what makes "last N" mean "most
 * recently visited" here.
 */
function capThreadViewState<T>(map: Readonly<Record<string, T>>): Record<string, T> {
    const entries = Object.entries(map);
    if (entries.length <= PERSISTED_THREAD_VIEW_LIMIT) return { ...map };
    return Object.fromEntries(entries.slice(-PERSISTED_THREAD_VIEW_LIMIT));
}

/**
 * Record the channel a conversation was left on, for the refresh restore.
 *
 * A `null` channel DELETES the key rather than storing null — see the field's
 * docblock. `unifiedId` is nullable because `setSelectedComposerChannel` takes
 * no id: it acts on whatever thread is open, and with none open there is
 * nothing to remember.
 */
function rememberChannel(
    map: Record<string, MessageChannel>,
    unifiedId: string | null,
    channel: MessageChannel | null,
): void {
    if (!unifiedId) return;
    if (channel === null) delete map[unifiedId];
    else map[unifiedId] = channel;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useInboxStore = create<IInboxUiState>()(
    persist(
        immer((set, get) => ({
            ...makeInitialState(),

            // --- Active conversation / composer ---------------------------------
            setActiveUnifiedConversationId: (id) =>
                set((s) => {
                    // Switching conversations always returns the compact view to
                    // the thread — never land on the previous contact's detail.
                    if (s.activeUnifiedConversationId !== id) {
                        s.compactContactOpen = false;
                        // A jump belongs to the thread it was clicked in.
                        s.pendingStarJump = null;
                        // …and always returns the composer to its collapsed
                        // default. Expansion is a per-VISIT intent, not a
                        // property of the conversation: because this map was
                        // never cleared, one Reply / Reply-all / AI-insert
                        // earlier in the session meant every later visit to
                        // that contact opened straight into a compose form,
                        // burying the thread the user came to read.
                        s.composerExpandedByThread = {};
                        // A quoted-reply target lives in the expanded composer
                        // (it is the strip above the text box). Collapsing the
                        // composer while keeping the target left it invisible
                        // and still armed: the next message sent on that
                        // thread would quote a message the agent could no
                        // longer see they were replying to.
                        s.replyTargetByThread = {};
                        // Same reasoning: a run belongs to the conversation
                        // it started in, and a stale "drafting" carried into
                        // the next thread would describe nothing.
                        s.aiDraftingByThread = {};
                    }
                    s.activeUnifiedConversationId = id;
                }),

            setComposerMode: (mode) =>
                set((s) => {
                    s.composerMode = mode;
                }),

            setContactPanelTab: (tab) =>
                set((s) => {
                    s.contactPanelTab = tab;
                }),

            setSelectedComposerChannel: (channel) =>
                set((s) => {
                    s.selectedComposerChannel = channel;
                    rememberChannel(
                        s.selectedChannelByConversation,
                        s.activeUnifiedConversationId,
                        channel,
                    );
                }),

            setActiveEmailThread: (unifiedId, threadId) =>
                set((s) => {
                    s.activeEmailThreadByConversation[unifiedId] = threadId;
                    // Choosing a chain always leaves the list — the two states
                    // are mutually exclusive views of the same pane.
                    s.emailThreadListOpenByConversation[unifiedId] = false;
                }),

            setEmailThreadListOpen: (unifiedId, open) =>
                set((s) => {
                    s.emailThreadListOpenByConversation[unifiedId] = open;
                }),

            setSelectedIdentity: (unifiedId, channelIdentityId) =>
                set((s) => {
                    s.selectedIdentityByThread[unifiedId] = channelIdentityId;
                }),

            setComposerExpanded: (unifiedId, expanded) =>
                set((s) => {
                    s.composerExpandedByThread[unifiedId] = expanded;
                }),

            // Deleted rather than set false: absent IS "not drafting", and a map
            // that only ever grows would keep one entry per conversation the
            // session ever saw an AI run on.
            setAiDrafting: (unifiedId, drafting) =>
                set((s) => {
                    if (drafting) s.aiDraftingByThread[unifiedId] = true;
                    else delete s.aiDraftingByThread[unifiedId];
                }),

            setComposeChannel: (channel) =>
                set((s) => {
                    s.composeChannel = channel;
                    // Closing (or switching) the panel retires whatever the old
                    // one reported — a stale `true` would keep warning about a
                    // draft that is no longer on screen.
                    s.composeDirty = false;
                }),

            setComposeDirty: (dirty) =>
                set((s) => {
                    s.composeDirty = dirty;
                }),

            expandComposerTo: (unifiedId, target) =>
                set((s) => {
                    s.composerExpandedByThread[unifiedId] = true;
                    if (target === 'note') {
                        s.composerMode = 'note';
                        // Reveal the Notes/Timeline tab where the note will land
                        // (mirrors ComposerChannelTabs' note selection).
                        s.contactPanelTab = 'notes';
                    } else {
                        s.composerMode = 'reply';
                        s.selectedComposerChannel = target;
                        rememberChannel(s.selectedChannelByConversation, unifiedId, target);
                    }
                }),

            selectComposerChannel: (unifiedId, target) =>
                set((s) => {
                    // Read BEFORE the write — this comparison is the whole rule.
                    const alreadyOnChannel = s.selectedComposerChannel === target;
                    s.composerMode = 'reply';
                    s.selectedComposerChannel = target;
                    rememberChannel(s.selectedChannelByConversation, unifiedId, target);
                    if (alreadyOnChannel) {
                        s.composerExpandedByThread[unifiedId] = true;
                    }
                }),

            setFilterRailOpen: (open) =>
                set((s) => {
                    s.filterRailOpen = open;
                }),

            setCompactContactOpen: (open) =>
                set((s) => {
                    s.compactContactOpen = open;
                }),

            setHighlightNoteId: (id) =>
                set((s) => {
                    s.highlightNoteId = id;
                }),

            requestComposerInsert: (insert) =>
                set((s) => {
                    s.composerExpandedByThread[insert.unifiedId] = true;
                    s.composerMode = 'reply';
                    s.selectedComposerChannel = insert.channel;
                    rememberChannel(
                        s.selectedChannelByConversation,
                        insert.unifiedId,
                        insert.channel,
                    );
                    // `castDraft` because the payload's address lists are
                    // `readonly` and immer's Draft type is not — same idiom the
                    // workflow builder store uses for its own readonly payloads.
                    s.pendingComposerInsert = castDraft(insert);
                }),

            clearComposerInsert: () =>
                set((s) => {
                    s.pendingComposerInsert = null;
                }),

            requestStarJump: (jump) =>
                set((s) => {
                    s.selectedComposerChannel = jump.channel;
                    if (jump.channel === 'EMAIL' && jump.emailThreadId) {
                        s.activeEmailThreadByConversation[jump.unifiedId] = jump.emailThreadId;
                        s.emailThreadListOpenByConversation[jump.unifiedId] = false;
                    }
                    // On a phone the panel covers the thread — go back to it.
                    s.compactContactOpen = false;
                    s.pendingStarJump = jump;
                }),

            clearStarJump: () =>
                set((s) => {
                    s.pendingStarJump = null;
                }),

            openMediaPreview: (unifiedId, files) =>
                set((s) => {
                    if (files.length === 0) return;
                    // Copied out of the readonly caller array: immer's draft
                    // type is mutable, and a `File` is not a plain object it
                    // can proxy.
                    s.pendingMediaSend = {
                        unifiedId,
                        files: [...files],
                        nonce: crypto.randomUUID(),
                    };
                }),

            clearMediaPreview: () =>
                set((s) => {
                    s.pendingMediaSend = null;
                }),

            startQuotedReply: (unifiedId, target) =>
                set((s) => {
                    s.composerExpandedByThread[unifiedId] = true;
                    s.composerMode = 'reply';
                    s.selectedComposerChannel = 'WHATSAPP';
                    s.replyTargetByThread[unifiedId] = target;
                }),

            clearQuotedReply: (unifiedId) =>
                set((s) => {
                    delete s.replyTargetByThread[unifiedId];
                }),

            setDraft: (unifiedId, channel, body) =>
                set((s) => {
                    s.drafts[draftKey(unifiedId, channel)] = body;
                }),

            clearDraft: (unifiedId, channel) =>
                set((s) => {
                    delete s.drafts[draftKey(unifiedId, channel)];
                }),

            setLastScheduledAt: (iso) =>
                set((s) => {
                    s.lastScheduledAt = iso;
                }),

            // --- Sticky tab snapshots ------------------------------------------
            enterTab: (tab, ids) =>
                set((s) => {
                    const snap = new Set(ids);
                    if (tab === 'unread') s.unreadTabSnapshot = snap;
                    else s.readTabSnapshot = snap;
                }),

            leaveTab: (tab) =>
                set((s) => {
                    if (tab === 'unread') s.unreadTabSnapshot = new Set();
                    else s.readTabSnapshot = new Set();
                }),

            // --- Dedup tape ----------------------------------------------------
            // O(1) membership via the Set — no array scan.
            hasSeenEvent: (eventId) => get().recentEvents.set.has(eventId),

            recordEvent: (eventId, eventAt) =>
                set((s) => {
                    if (s.recentEvents.set.has(eventId)) return;
                    // Rebuild both structures and replace the holder wholesale so
                    // the Set and the FIFO queue can never drift apart.
                    const nextSet = new Set(s.recentEvents.set);
                    nextSet.add(eventId);
                    const nextQueue = [...s.recentEvents.queue, eventId];
                    // FIFO eviction: drop the oldest ids from BOTH structures
                    // once the tape exceeds the cap.
                    while (nextQueue.length > RECENT_EVENT_IDS_LIMIT) {
                        const evicted = nextQueue.shift();
                        if (evicted !== undefined) nextSet.delete(evicted);
                    }
                    s.recentEvents = { set: nextSet, queue: nextQueue };
                    if (!s.lastEventAt || eventAt > s.lastEventAt) {
                        s.lastEventAt = eventAt;
                    }
                }),

            // --- Reset ---------------------------------------------------------
            reset: () =>
                set((s) => {
                    Object.assign(s, makeInitialState());
                }),
        })),
        {
            name: 'inbox-ui',
            storage: createJSONStorage(() => sessionStorage),
            /**
             * The last-event timestamps, plus which email chain the user was
             * looking at.
             *
             * Every `Set`-backed field (the dedup tape, the tab snapshots) stays
             * ephemeral by design — Sets don't round-trip via JSON, and the
             * `lastEventAt` watermark alone drives the reconnect sync replay.
             *
             * The two email maps are here because "no chain selected yet" and
             * "the user pressed back to browse the list" are different states,
             * and only the first should auto-open the newest chain. Losing that
             * distinction on refresh is what made F5 jump the user out of the
             * chain list and into the first thread — marking it read on the way.
             * `sessionStorage`, so these die with the tab rather than growing in
             * localStorage forever, and `capThreadViewState` bounds what is
             * written on top of that.
             *
             * `selectedChannelByConversation` joined them for the same reason one
             * level up: those two only matter once the Email reader is on screen,
             * and which reader is on screen is the channel. Persisting the chain
             * but not the channel left the restored chain unreachable.
             */
            partialize: (s) => ({
                lastEventAt: s.lastEventAt,
                lastScheduledAt: s.lastScheduledAt,
                selectedChannelByConversation: capThreadViewState(s.selectedChannelByConversation),
                activeEmailThreadByConversation: capThreadViewState(
                    s.activeEmailThreadByConversation,
                ),
                emailThreadListOpenByConversation: capThreadViewState(
                    s.emailThreadListOpenByConversation,
                ),
            }),
        },
    ),
);
